/**
 * @author mschuetz / http://mschuetz.at
 *
 * adapted from THREE.OrbitControls by
 *
 * @author qiao / https://github.com/qiao
 * @author mrdoob / http://mrdoob.com
 * @author alteredq / http://alteredqualia.com/
 * @author WestLangley / http://github.com/WestLangley
 * @author erich666 / http://erichaines.com
 *
 *
 *
 */

import * as THREE from '../../libs/three.js/build/three.module.js';
import { MOUSE } from '../defines.js';
import { Utils } from '../utils.js';
import { EventDispatcher } from '../EventDispatcher.js';
import { NAV_SNAP_SOURCES } from '../utils/SharedUtils.js';
import { orbitWheelZoomDelta } from './orbitWheelZoom.js';

export class OrbitControls extends EventDispatcher {
  constructor(viewer) {
    super();

    this.viewer = viewer;
    this.renderer = viewer.renderer;

    this.scene = null;
    this.sceneControls = new THREE.Scene();

    // Bound view/camera override the scene defaults. Used by multi-view rendering
    // where each pane has its own View and Camera. Falls back to scene.view /
    // scene.getActiveCamera() when null, preserving the single-view behaviour.
    this.boundView = null;
    this.boundCamera = null;

    this.rotationSpeed = 5;

    this.fadeFactor = 20;
    this.yawDelta = 0;
    this.pitchDelta = 0;
    this.panDelta = new THREE.Vector2(0, 0);
    this.radiusDelta = 0;

    this.doubleClickZoomEnabled = true;
    this.scrollEnabled = true;

    this.tweens = [];

    let drag = (e) => {
      if (e.drag.object !== null) {
        return;
      }

      if (e.drag.startHandled === undefined) {
        e.drag.startHandled = true;

        this.dispatchEvent({ type: 'start' });
      }

      let ndrag = {
        x: e.drag.lastDrag.x / this.renderer.domElement.clientWidth,
        y: e.drag.lastDrag.y / this.renderer.domElement.clientHeight,
      };

      if (e.drag.mouse === MOUSE.LEFT) {
        this.yawDelta += ndrag.x * this.rotationSpeed;
        this.pitchDelta += ndrag.y * this.rotationSpeed;

        this.stopTweens();
      } else if (e.drag.mouse === MOUSE.MIDDLE) {
        if (this.getActiveView().radius > 0) {
          this.panDelta.x += ndrag.x;
          this.panDelta.y += ndrag.y;
        } else {
          this.panDelta.x -= ndrag.x;
          this.panDelta.y -= ndrag.y;
        }

        this.stopTweens();
      }
    };

    let drop = (e) => {
      this.dispatchEvent({ type: 'end' });
    };

    let scroll = (e) => {
      let resolvedRadius = this.getActiveView().radius + this.radiusDelta;
      this.radiusDelta += orbitWheelZoomDelta(
        resolvedRadius,
        e.delta,
        this.scrollEnabled,
      );

      this.stopTweens();
    };

    let dblclick = (e) => {
      if (this.doubleClickZoomEnabled) {
        this.zoomToLocation(e.mouse);
      }
    };

    let previousTouch = null;
    let touchStart = (e) => {
      // Don't process touch events for navigation in measure mode
      if (viewer.measureModeEnabled) {
        return;
      }
      
      // Store touch positions as plain objects (not Touch objects which are read-only)
      previousTouch = {
        touches: Array.from(e.touches).map(t => ({
          pageX: t.pageX,
          pageY: t.pageY
        }))
      };
    };

    let touchEnd = (e) => {
      // Don't process touch events for navigation in measure mode
      if (viewer.measureModeEnabled) {
        return;
      }
      
      // Update touch positions if touches still exist, otherwise keep previous state
      if (e.touches && e.touches.length > 0) {
        previousTouch = {
          touches: Array.from(e.touches).map(t => ({
            pageX: t.pageX,
            pageY: t.pageY
          }))
        };
      }
    };

    let touchMove = (e) => {
      // Don't process touch events for navigation in measure mode
      if (viewer.measureModeEnabled) {
        return;
      }
      
      // 1 finger drag - rotation (handled by drag event from InputHandler emulation)
      // But we need to store previousTouch for multi-touch gestures
      if (e.touches.length === 1 && previousTouch && previousTouch.touches.length === 1) {
        // Single finger rotation is handled through drag events emulated by InputHandler
        // No special handling needed here, but we keep track of touch state
      } else if (e.touches.length === 2 && previousTouch && previousTouch.touches.length === 2) {
        // 2 finger pinch - zoom
        let prev = previousTouch;
        let curr = e;

        let prevDX = prev.touches[0].pageX - prev.touches[1].pageX;
        let prevDY = prev.touches[0].pageY - prev.touches[1].pageY;
        let prevDist = Math.sqrt(prevDX * prevDX + prevDY * prevDY);

        let currDX = curr.touches[0].pageX - curr.touches[1].pageX;
        let currDY = curr.touches[0].pageY - curr.touches[1].pageY;
        let currDist = Math.sqrt(currDX * currDX + currDY * currDY);

        let delta = currDist / prevDist;
        let resolvedRadius = this.getActiveView().radius + this.radiusDelta;
        let newRadius = resolvedRadius / delta;
        this.radiusDelta = newRadius - resolvedRadius;

        this.stopTweens();
      } else if (e.touches.length === 3 && previousTouch && previousTouch.touches.length === 3) {
        // 3 finger drag - pan
        let prev = previousTouch;
        let curr = e;

        let prevMeanX =
          (prev.touches[0].pageX +
            prev.touches[1].pageX +
            prev.touches[2].pageX) /
          3;
        let prevMeanY =
          (prev.touches[0].pageY +
            prev.touches[1].pageY +
            prev.touches[2].pageY) /
          3;

        let currMeanX =
          (curr.touches[0].pageX +
            curr.touches[1].pageX +
            curr.touches[2].pageX) /
          3;
        let currMeanY =
          (curr.touches[0].pageY +
            curr.touches[1].pageY +
            curr.touches[2].pageY) /
          3;

        let delta = {
          x: (currMeanX - prevMeanX) / this.renderer.domElement.clientWidth,
          y: (currMeanY - prevMeanY) / this.renderer.domElement.clientHeight,
        };

        this.panDelta.x += delta.x;
        this.panDelta.y += delta.y;

        this.stopTweens();
      }

      // Store current touch state for next move
      if (previousTouch === null || e.touches.length !== previousTouch.touches.length) {
        previousTouch = {
          touches: Array.from(e.touches).map(t => ({
            pageX: t.pageX,
            pageY: t.pageY
          }))
        };
      } else {
        // Update existing
        for (let i = 0; i < e.touches.length && i < previousTouch.touches.length; i++) {
          previousTouch.touches[i].pageX = e.touches[i].pageX;
          previousTouch.touches[i].pageY = e.touches[i].pageY;
        }
      }
    };

    this.addEventListener('touchstart', touchStart);
    this.addEventListener('touchend', touchEnd);
    this.addEventListener('touchmove', touchMove);
    this.addEventListener('drag', drag);
    this.addEventListener('drop', drop);
    this.addEventListener('mousewheel', scroll);
    this.addEventListener('dblclick', dblclick);
  }

  setScene(scene) {
    this.scene = scene;
  }

  /**
   * Resolve which View these controls operate on.
   * Multi-view rendering sets `boundView` per pane; single-view falls through
   * to `scene.view`. Returns null if neither is available.
   */
  getActiveView() {
    return this.boundView ?? (this.scene ? this.scene.view : null);
  }

  /**
   * Resolve which camera these controls reference for picking / look-at.
   * Multi-view rendering sets `boundCamera` per pane; single-view falls through
   * to `scene.getActiveCamera()`. Returns null if neither is available.
   */
  getActiveCamera() {
    return this.boundCamera ?? (this.scene ? this.scene.getActiveCamera() : null);
  }

  stop() {
    this.yawDelta = 0;
    this.pitchDelta = 0;
    this.radiusDelta = 0;
    this.panDelta.set(0, 0);
  }

  async zoomToLocation(mouse) {
    let camera = this.getActiveCamera();
    let view = this.getActiveView();

    let I = await Utils.getMousePointCloudIntersectionAsync(
      mouse,
      camera,
      this.viewer,
      this.scene.pointclouds,
      { pickClipped: true, snapSources: NAV_SNAP_SOURCES }
    );

    if (I === null) {
      return;
    }

    let targetRadius = 0;
    {
      let minimumJumpDistance = 0.2;

      if (I.pointcloud) {
        let domElement = this.renderer.domElement;
        let ray = Utils.mouseToRay(
          mouse,
          camera,
          domElement.clientWidth,
          domElement.clientHeight
        );

        let nodes = I.pointcloud.nodesOnRay(I.pointcloud.visibleNodes, ray);
        let lastNode = nodes[nodes.length - 1];
        let radius = lastNode.getBoundingSphere(new THREE.Sphere()).radius;
        targetRadius = Math.min(view.radius, radius);
      } else {
        // Mesh / BIM hits carry no octree nodes to size the jump from.
        targetRadius = Math.min(view.radius, I.distance * 0.1);
      }
      targetRadius = Math.max(minimumJumpDistance, targetRadius);
    }

    let d = view.direction.multiplyScalar(-1);
    let cameraTargetPosition = new THREE.Vector3().addVectors(
      I.location,
      d.multiplyScalar(targetRadius)
    );
    // TODO Unused: let controlsTargetPosition = I.location;

    let animationDuration = 600;
    let easing = TWEEN.Easing.Quartic.Out;

    {
      // animate
      let value = { x: 0 };
      let tween = new TWEEN.Tween(value).to({ x: 1 }, animationDuration);
      tween.easing(easing);
      this.tweens.push(tween);

      let startPos = view.position.clone();
      let targetPos = cameraTargetPosition.clone();
      let startRadius = view.radius;
      let targetRadius = cameraTargetPosition.distanceTo(I.location);

      tween.onUpdate(() => {
        let t = value.x;
        view.position.x = (1 - t) * startPos.x + t * targetPos.x;
        view.position.y = (1 - t) * startPos.y + t * targetPos.y;
        view.position.z = (1 - t) * startPos.z + t * targetPos.z;

        view.radius = (1 - t) * startRadius + t * targetRadius;
        if (view.radius >= 0) {
          this.viewer.setMoveSpeed(Math.abs(view.radius));
        } else {
          this.viewer.setMoveSpeed(0);
        }
      });

      tween.onComplete(() => {
        this.tweens = this.tweens.filter((e) => e !== tween);
      });

      tween.start();
    }
  }

  stopTweens() {
    this.tweens.forEach((e) => e.stop());
    this.tweens = [];
  }

  update(delta) {
    let view = this.getActiveView();
    if (!view) return;

    {
      // apply rotation
      let progression = Math.min(1, this.fadeFactor * delta);

      let yaw = view.yaw;
      let pitch = view.pitch;
      let pivot = view.getPivot();

      yaw -= progression * this.yawDelta;
      pitch -= progression * this.pitchDelta;

      view.yaw = yaw;
      view.pitch = pitch;

      let V = view.direction.multiplyScalar(-view.radius);
      let position = new THREE.Vector3().addVectors(pivot, V);

      view.position.copy(position);
    }

    {
      // apply pan
      let progression = Math.min(1, this.fadeFactor * delta);
      let panDistance = progression * view.radius * 3;

      let px = -this.panDelta.x * panDistance;
      let py = this.panDelta.y * panDistance;

      view.pan(px, py);
    }

    {
      // apply zoom
      let progression = Math.min(1, this.fadeFactor * delta);

      // let radius = view.radius + progression * this.radiusDelta * view.radius * 0.1;
      let radius = view.radius + progression * this.radiusDelta;

      if (view.radius + radius < 0) {
        this.stop();
        let V = view.direction.multiplyScalar(0.5);
        let position = new THREE.Vector3().addVectors(view.getPivot(), V);
        view.radius = 0.5;
        view.position.copy(position);
      } else {
        let V = view.direction.multiplyScalar(-radius);
        let position = new THREE.Vector3().addVectors(view.getPivot(), V);
        view.radius = radius;
        view.position.copy(position);
      }
    }

    {
      let speed = view.radius;
      if (speed >= 0) {
        this.viewer.setMoveSpeed(Math.abs(speed));
      } else {
        this.viewer.setMoveSpeed(0);
      }
    }

    {
      // decelerate over time
      let progression = Math.min(1, this.fadeFactor * delta);
      let attenuation = Math.max(0, 1 - this.fadeFactor * delta);

      this.yawDelta *= attenuation;
      this.pitchDelta *= attenuation;
      this.panDelta.multiplyScalar(attenuation);
      // this.radiusDelta *= attenuation;
      this.radiusDelta -= progression * this.radiusDelta;
    }
  }
}
